import { Accordion } from '@material-ui/core';
import {
  CheckCircle,
  HourglassFull,
  AddCircleOutline
} from '@material-ui/icons';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '../../Auth0Provider';
import { sharedRepStorageKey, AmplitudeEventKeys } from '../../Constants';
import { RepItem } from '../../redux/models/shareDataReps';
import { InsightDispatch, iRootState } from '../../redux/store';
import ShareSectionHeader from './ShareSectionHeader';
import ShareSectionList from './ShareSectionList';
import RepMfrDataAccessStatus from '../../RepMfrDataAccessStatuses';
import trackAmplitudeEvent from './trackAmplitudeEvents';

const RepsRequestDataSelector = function RepsRequestDataSelector(): React.ReactElement | null {
  const vendorId = useSelector(
    (state: iRootState) => state.companyInfo.vendorId
  );
  const allAvailableReps = useSelector(
    (state: iRootState) => state.shareDataReps.reps
  );

  const dispatch = useDispatch<InsightDispatch>();
  const { dataAccessToken } = useAuth0();

  const getReps = useCallback(() => {
    dispatch.shareDataReps.getAvailableReps({
      mfrId: vendorId,
      dataAccessToken
    });
  }, [vendorId, dataAccessToken, dispatch]);

  const getSharedReps = function getSharedReps(): RepItem[] {
    const previousSharedReps = localStorage.getItem(sharedRepStorageKey);
    if (previousSharedReps) {
      return JSON.parse(previousSharedReps);
    }
    return [];
  };

  const selectableReps = allAvailableReps
    .filter(
      r =>
        r.repStatus !== RepMfrDataAccessStatus.Approved &&
        r.repStatus !== RepMfrDataAccessStatus.Pending
    )
    .map(r => ({
      id: r.repId,
      text: r.repName,
      preselected: r.repStatus === RepMfrDataAccessStatus.Pending,
      isPending: r.repStatus === RepMfrDataAccessStatus.Pending
    }));

  const oldSharedReps = getSharedReps();
  const approvedReps = allAvailableReps
    .filter(r => r.repStatus === RepMfrDataAccessStatus.Approved)
    .map(r => ({
      id: r.repId,
      text: r.repName,
      preselected: false,
      isNew: !oldSharedReps.some(old => old.repId === r.repId)
    }));

  const pendingReps = allAvailableReps
    .filter(r => r.repStatus === RepMfrDataAccessStatus.Pending)
    .map(r => ({
      id: r.repId,
      text: r.repName,
      preselected: false,
      isPending: true
    }));

  const newSharedReps = approvedReps.filter(
    value => !oldSharedReps.map(a => a.repId).includes(value.id)
  );

  const updateStatusData = function updateStatusData(
    status: string,
    event: string
  ): (ids: string[]) => Promise<void> {
    return async (ids: string[]): Promise<void> => {
      dispatch.loading.setLoading(true);

      try {
        const repStatusList = ids.map(r => ({
          repId: r,
          status
        }));

        await dispatch.shareDataReps.shareData({
          repStatusList,
          dataAccessToken
        });
        trackAmplitudeEvent(ids, event, dispatch);
        getReps();
      } finally {
        dispatch.loading.setLoading(false);
      }
    };
  };

  const approvedRepsActions = [
    {
      text: 'Stop Receiving',
      callback: updateStatusData(
        RepMfrDataAccessStatus.RevokedByMfr,
        AmplitudeEventKeys.StopReceivingButtonClicked
      )
    }
  ];

  const selectableRepsActions = [
    {
      text: 'Request Data',
      callback: updateStatusData(
        RepMfrDataAccessStatus.Pending,
        AmplitudeEventKeys.RequestButtonClicked
      )
    }
  ];

  const pendingRepsActions = [
    {
      text: 'Cancel Request',
      callback: updateStatusData(
        RepMfrDataAccessStatus.RevokedByRep,
        AmplitudeEventKeys.RevokeButtonClicked
      )
    }
  ];

  useEffect(() => {
    const saveSharedReps = function saveSharedReps(
      approvedRepsArg: RepItem[]
    ): void {
      const sharedReps = JSON.stringify([...approvedRepsArg]);
      localStorage.setItem(sharedRepStorageKey, sharedReps);
    };
    saveSharedReps(
      approvedReps.map(r => ({
        repId: r.id,
        repName: r.text,
        repStatus: RepMfrDataAccessStatus.Approved
      }))
    );
  }, [approvedReps]);

  useEffect(() => {
    getReps();
  }, [getReps]);

  return (
    <>
      <Accordion style={{ padding: 0, margin: 0 }}>
        <ShareSectionHeader
          label='Receiving Data'
          count={approvedReps.length}
          newCount={newSharedReps.length}
          icon={<CheckCircle />}
        />
        <ShareSectionList
          items={approvedReps}
          actions={approvedRepsActions}
          emptyLabel='You are not receiving data from any rep yet. Request data from the section below.'
        />
      </Accordion>

      <Accordion defaultExpanded style={{ padding: 0, margin: 0 }}>
        <ShareSectionHeader
          label='Pending Requests'
          count={pendingReps.length}
          icon={<HourglassFull />}
        />
        <ShareSectionList
          items={pendingReps}
          actions={pendingRepsActions}
          emptyLabel='No pending requests. Your requests will appear here until reps approve them'
        />
      </Accordion>

      <Accordion style={{ padding: 0, margin: 0 }}>
        <ShareSectionHeader
          label='Available to Request'
          count={selectableReps.length}
          icon={<AddCircleOutline />}
        />
        <ShareSectionList
          items={selectableReps}
          actions={selectableRepsActions}
          emptyLabel='No reps available to request data from.'
        />
      </Accordion>
    </>
  );
};

export default RepsRequestDataSelector;
